import React, { type JSX } from "react";
import UploadTable from "../../UploadTable_Component";
import GenerationProgressBar from "../../GenerationProgressBar_Component";
import InlineResultsTabs from "../../InlineResultsTabs_Component";
import BotMessageContent from "./BotMessageContent";
import ChatFactFooter from "./ChatFactFooter";
import {
  UploadReadyPrompt,
  UploadStatusMessage,
  SuggestedActions,
  FeedbackControls,
} from "./ChatMessageParts";

export interface BotMessageBodyProps {
  message: any;
  index: number;
  allFacts: any[];
  allDocuments: any[];
  onDocumentClick: (document: any) => void;
  isLatestBot: boolean;
  feedbackChoice?: "thumbs_up" | "thumbs_down";
  isScaling: boolean;
  onFeedback: (index: number, feedbackType: "thumbs_up" | "thumbs_down") => void;
}

/** The default bot message body: streamed text, results, sources and feedback. */
function BotTextMessage({
  message,
  index,
  allFacts,
  allDocuments,
  onDocumentClick,
  isLatestBot,
  feedbackChoice,
  isScaling,
  onFeedback,
}: BotMessageBodyProps): JSX.Element {
  const suggestedActions = Array.isArray(message.suggested_actions)
    ? message.suggested_actions
    : [];

  return (
    <div>
      <div className="text-sm sm:text-base leading-relaxed break-words text-cvc-text">
        {/* Phase 7: Use new BotMessageContent component with useStreamingMessage hook */}
        <BotMessageContent message={message} allFacts={allFacts} allDocuments={allDocuments} />
      </div>
      {/* Inline results (deals + research docs) — rendered per-turn when feature flag is on */}
      <InlineResultsTabs turnId={message.turnId} />
      {/* Fact footer - shows source documents referenced in this message */}
      <ChatFactFooter
        message={message}
        onDocumentClick={onDocumentClick}
        allFacts={allFacts}
        allDocuments={allDocuments}
      />
      {suggestedActions.length > 0 && <SuggestedActions actions={suggestedActions} />}
      {/* Feedback controls - only for the latest bot message with a turnId */}
      {isLatestBot && message.turnId && (
        <FeedbackControls
          index={index}
          chosen={feedbackChoice}
          scaling={isScaling}
          onFeedback={onFeedback}
        />
      )}
    </div>
  );
}

/** Bot messages: full-width without bubble, dispatched on the message type. */
export default function BotMessageBody(props: BotMessageBodyProps): JSX.Element {
  const { message } = props;

  if (message.type === "generation-progress") {
    return (
      <GenerationProgressBar
        projectId={message.projectId as string}
        startTime={message.startTime as number}
        status={message.status as "generating" | "processed" | "failed" | "timeout"}
        error={message.error as string | undefined}
      />
    );
  }

  if (message.type === "upload-table") {
    return <UploadTable uploads={Object.values(message.uploads || {})} />;
  }

  if (message.type === "upload-ready-prompt") {
    return <UploadReadyPrompt text={message.text} />;
  }

  if (message.type === "upload-status") {
    return <UploadStatusMessage message={message} />;
  }

  return <BotTextMessage {...props} />;
}
